import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import ProductCard from '../components/ProductCard';
import { SlidersHorizontal } from 'lucide-react';
import { API_URL } from '../config';
import img1 from '../assets/1.png';
import img2 from '../assets/2.png';
import img3 from '../assets/3.png';

const HomeScreen = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [searchParams] = useSearchParams();
  const search = searchParams.get('search') || '';
  const [currentSlide, setCurrentSlide] = useState(0);
  const [heroImages, setHeroImages] = useState([img1, img2, img3]);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/settings`);
        if (data && data.heroImages && data.heroImages.length > 0) {
          setHeroImages(data.heroImages);
        }
      } catch (err) {
        console.error('Error fetching settings:', err);
      }
    };
    fetchSettings();
  }, []);

  useEffect(() => {
    if (heroImages.length <= 1) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroImages.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [heroImages.length]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/categories`);
        setCategories(data);
      } catch (err) {
        console.error('Error fetching categories:', err);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        let url = `${API_URL}/products?`;
        if (activeCategory) url += `category=${activeCategory}&`;
        if (search) url += `search=${search}&`;
        if (sortBy) url += `sort=${sortBy}&`;
        const { data } = await axios.get(url);
        setProducts(data);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [activeCategory, search, sortBy]);

  return (
    <div className="container mx-auto px-4 py-10 space-y-8">
      {/* Hero / Promo Section */}
      <div className="mb-10">
        {/* Mobile View (Slideshow) */}
        <div className="block md:hidden relative rounded-2xl overflow-hidden shadow-lg w-full aspect-[4/3] sm:aspect-[16/9]">
          <div 
            className="flex w-full h-full transition-transform duration-500 ease-in-out"
            style={{ transform: `translateX(-${currentSlide * 100}%)` }}
          >
            {heroImages.map((img, idx) => (
              <img key={idx} src={img} alt={`Promotion ${idx + 1}`} className="w-full h-full object-cover flex-shrink-0" />
            ))}
          </div>
          {/* Dots */}
          <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2">
            {heroImages.map((_, idx) => (
              <button 
                key={idx}
                onClick={() => setCurrentSlide(idx)}
                className={`w-2.5 h-2.5 rounded-full transition-colors ${currentSlide === idx ? 'bg-primary-600 shadow-md scale-110' : 'bg-white/70 hover:bg-white'}`}
              />
            ))}
          </div>
        </div>

        {/* Desktop View (Grid) */}
        <div className={`hidden md:grid gap-6 ${
          heroImages.length === 1 
            ? 'grid-cols-1' 
            : heroImages.length === 2 
            ? 'grid-cols-2' 
            : 'grid-cols-3'
        }`}>
          {heroImages.map((img, idx) => (
            <div key={idx} className="rounded-2xl overflow-hidden shadow-lg transition-transform hover:scale-[1.02] aspect-[4/3] lg:aspect-[16/9] bg-secondary-100">
              <img 
                src={img} 
                alt={`Promotion ${idx + 1}`} 
                className="w-full h-full object-cover" 
                onError={(e) => {
                  const defaults = [img1, img2, img3];
                  e.target.src = defaults[idx % defaults.length];
                }}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Category Pills */}
      {categories.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-sm font-bold text-secondary-500 uppercase tracking-widest">Shop by Category</h3>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setActiveCategory('')}
              className={`px-5 py-2.5 rounded-full text-sm font-bold transition-all ${
                !activeCategory
                  ? 'bg-primary-600 text-white shadow-lg shadow-primary-200'
                  : 'bg-white text-secondary-600 border border-secondary-200 hover:border-primary-300'
              }`}
            >
              All Products
            </button>
            {categories.map((cat) => (
              <button
                key={cat._id}
                onClick={() => setActiveCategory(cat._id)}
                className={`px-5 py-2.5 rounded-full text-sm font-bold transition-all ${
                  activeCategory === cat._id
                    ? 'bg-primary-600 text-white shadow-lg shadow-primary-200'
                    : 'bg-white text-secondary-600 border border-secondary-200 hover:border-primary-300'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Sort & Title */}
      <div className="flex items-end justify-between">
        <div className="space-y-2">
          <h2 className="text-3xl font-bold text-secondary-900">
            {search ? `Results for "${search}"` : 'Latest Products'}
          </h2>
          <div className="h-1.5 w-20 bg-primary-500 rounded-full" />
        </div>
        <div className="flex items-center space-x-3">
          <SlidersHorizontal className="h-4 w-4 text-secondary-400" />
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="bg-white border border-secondary-200 rounded-xl py-2 px-4 text-sm font-medium text-secondary-700 focus:ring-2 focus:ring-primary-500 outline-none"
          >
            <option value="">Latest</option>
            <option value="price_asc">Price: Low to High</option>
            <option value="price_desc">Price: High to Low</option>
            <option value="name">Name: A-Z</option>
            <option value="rating">Top Rated</option>
          </select>
        </div>
      </div>

      {/* Products Grid */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-secondary-100 animate-pulse rounded-2xl aspect-[4/5]" />
          ))}
        </div>
      ) : products.length === 0 ? (
        <div className="text-center py-20 space-y-4">
          <p className="text-2xl font-bold text-secondary-400">No products found</p>
          <p className="text-secondary-500">Try a different search or category.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
          {products.map((product) => (
            <ProductCard key={product._id} product={product} />
          ))}
        </div>
      )}
    </div>
  );
};

export default HomeScreen;
